/**
 * Super Admin Inquiries Page - 전체 문의 관리
 * 
 * Phase 6: 슈퍼어드민 영업 지원
 * - 모든 워크스페이스의 문의 목록
 * - 필터링 (상태, 유형)
 * - 문의 상세 및 답변
 */

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { MessageCircle, Loader2, CheckCircle, Clock, AlertCircle, Search, Filter, Send, StickyNote } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { cn } from '../../lib/utils';
import { Fire } from '../../services/FireClient';
import { collection, getDocs, query, orderBy, doc, updateDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { Inquiry, InquiryStatus, InquiryType, InquiryMessage, INQUIRY_TYPE_LABELS, INQUIRY_STATUS_LABELS } from '../../models/Inquiry';

const SuperAdminInquiriesPage: React.FC = () => {
    const { i18n } = useLingui();
    const [loading, setLoading] = useState(true);
    const [inquiries, setInquiries] = useState<Inquiry[]>([]);
    const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isMemoOpen, setIsMemoOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [internalNote, setInternalNote] = useState('');
    const [editedStatus, setEditedStatus] = useState<InquiryStatus>('new');
    const [newMessage, setNewMessage] = useState('');

    // Filters
    const [statusFilter, setStatusFilter] = useState<InquiryStatus | 'all'>('all');
    const [typeFilter, setTypeFilter] = useState<InquiryType | 'all'>('all');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        loadAllInquiries();
    }, []);

    const loadAllInquiries = async () => {
        setLoading(true);
        try {
            // 전역 inquiries 컬렉션에서 로드 (collectionGroup 대신)
            const q = query(
                collection(Fire.store, 'inquiries'),
                orderBy('createdAt', 'desc')
            );
            const snap = await getDocs(q);
            const data = snap.docs.map(d => ({
                id: d.id,
                ...d.data(),
                createdAt: d.data().createdAt?.toDate?.() || new Date(),
                updatedAt: d.data().updatedAt?.toDate?.() || new Date(),
            })) as Inquiry[];
            setInquiries(data);
        } catch (e) {
            console.error('Failed to load inquiries:', e);
        } finally {
            setLoading(false);
        }
    };

    // 목록에서 직접 상태 변경
    const handleStatusChange = async (inquiryId: string, workspaceId: string, newStatus: InquiryStatus) => {
        try {
            const ref = doc(Fire.store, 'inquiries', inquiryId);
            await updateDoc(ref, {
                status: newStatus,
                updatedAt: serverTimestamp(),
            });
            if (workspaceId) {
                const wsRef = doc(Fire.store, 'workspaces', workspaceId, 'inquiries', inquiryId);
                await updateDoc(wsRef, {
                    status: newStatus,
                    updatedAt: serverTimestamp(),
                }).catch(() => { });
            }
            loadAllInquiries();
        } catch (e) {
            console.error('Failed to update status:', e);
        }
    };

    // 메모 저장 (메모 모달에서)
    const handleSaveMemo = async () => {
        if (!selectedInquiry) return;
        setSaving(true);
        try {
            const ref = doc(Fire.store, 'inquiries', selectedInquiry.id);
            await updateDoc(ref, {
                internalNote,
                updatedAt: serverTimestamp(),
            });
            setIsMemoOpen(false);
            loadAllInquiries();
            alert(t(i18n)`Memo saved`);
        } catch (e) {
            console.error('Failed to save memo:', e);
            alert(t(i18n)`Failed to save`);
        } finally {
            setSaving(false);
        }
    };

    // 상태 저장 (답변 모달에서)
    const handleSaveStatus = async () => {
        if (!selectedInquiry) return;
        setSaving(true);
        try {
            const ref = doc(Fire.store, 'inquiries', selectedInquiry.id);
            await updateDoc(ref, {
                status: editedStatus,
                updatedAt: serverTimestamp(),
            });
            if (selectedInquiry.workspaceId) {
                const wsRef = doc(Fire.store, 'workspaces', selectedInquiry.workspaceId, 'inquiries', selectedInquiry.id);
                await updateDoc(wsRef, {
                    status: editedStatus,
                    updatedAt: serverTimestamp(),
                }).catch(() => { });
            }
            setIsDetailOpen(false);
            setSelectedInquiry(null);
            setNewMessage('');
            loadAllInquiries();
        } catch (e) {
            console.error('Failed to save status:', e);
            alert(t(i18n)`Failed to save`);
        } finally {
            setSaving(false);
        }
    };

    // 쓰레드 메시지 전송
    const handleSendMessage = async () => {
        if (!selectedInquiry || !newMessage.trim()) return;
        setSaving(true);
        try {
            const message: InquiryMessage = {
                id: `msg_${Date.now()}`,
                content: newMessage.trim(),
                authorId: 'super_admin',
                authorName: '運営チーム',
                authorRole: 'admin',
                createdAt: new Date(),
            };

            // 전역 문서에 메시지 추가
            const ref = doc(Fire.store, 'inquiries', selectedInquiry.id);
            await updateDoc(ref, {
                messages: arrayUnion(message),
                status: editedStatus === 'new' ? 'in_progress' : editedStatus,
                updatedAt: serverTimestamp(),
            });

            // 워크스페이스 문서에도 메시지 추가 (고객이 볼 수 있도록)
            if (selectedInquiry.workspaceId) {
                const wsRef = doc(Fire.store, 'workspaces', selectedInquiry.workspaceId, 'inquiries', selectedInquiry.id);
                await updateDoc(wsRef, {
                    messages: arrayUnion(message),
                    status: editedStatus === 'new' ? 'in_progress' : editedStatus,
                    updatedAt: serverTimestamp(),
                }).catch(() => { });
            }

            // 로컬 상태 업데이트
            setSelectedInquiry({
                ...selectedInquiry,
                messages: [...(selectedInquiry.messages || []), message],
                status: editedStatus === 'new' ? 'in_progress' : editedStatus,
            });
            setNewMessage('');
            if (editedStatus === 'new') setEditedStatus('in_progress');
            loadAllInquiries();
        } catch (e) {
            console.error('Failed to send message:', e);
            alert(t(i18n)`Failed to send`);
        } finally {
            setSaving(false);
        }
    };

    const getStatusBadge = (status: InquiryStatus) => {
        const colors = {
            new: 'bg-blue-100 text-blue-700',
            in_progress: 'bg-amber-100 text-amber-700',
            resolved: 'bg-emerald-100 text-emerald-700',
        };
        const icons = {
            new: <AlertCircle className="w-3 h-3" />,
            in_progress: <Clock className="w-3 h-3" />,
            resolved: <CheckCircle className="w-3 h-3" />,
        };
        return (
            <Badge className={cn("flex items-center gap-1", colors[status])}>
                {icons[status]}
                {i18n._(INQUIRY_STATUS_LABELS[status])}
            </Badge>
        );
    };

    const formatDate = (date: any) => {
        const d = date?.toDate ? date.toDate() : new Date(date);
        return d.toLocaleDateString('ja-JP', {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    // Filter inquiries
    const filtered = inquiries.filter(i => {
        if (statusFilter !== 'all' && i.status !== statusFilter) return false;
        if (typeFilter !== 'all' && i.type !== typeFilter) return false;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            return (
                i.subject.toLowerCase().includes(q) ||
                i.message.toLowerCase().includes(q) ||
                i.workspaceName?.toLowerCase().includes(q)
            );
        }
        return true;
    });

    // Stats
    const stats = {
        total: inquiries.length,
        new: inquiries.filter(i => i.status === 'new').length,
        inProgress: inquiries.filter(i => i.status === 'in_progress').length,
        resolved: inquiries.filter(i => i.status === 'resolved').length,
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                    <MessageCircle className="w-6 h-6 text-indigo-500" />
                    <Trans>Inquiry Management</Trans>
                </h1>
                <p className="text-slate-500 mt-1">
                    <Trans>Inquiries from all workspaces</Trans>
                </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
                <Card className="p-4">
                    <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
                    <div className="text-sm text-slate-500">全件</div>
                </Card>
                <Card className="p-4 border-blue-200 bg-blue-50">
                    <div className="text-2xl font-bold text-blue-700">{stats.new}</div>
                    <div className="text-sm text-blue-600">新規</div>
                </Card>
                <Card className="p-4 border-amber-200 bg-amber-50">
                    <div className="text-2xl font-bold text-amber-700">{stats.inProgress}</div>
                    <div className="text-sm text-amber-600">対応中</div>
                </Card>
                <Card className="p-4 border-emerald-200 bg-emerald-50">
                    <div className="text-2xl font-bold text-emerald-700">{stats.resolved}</div>
                    <div className="text-sm text-emerald-600">完了</div>
                </Card>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    placeholder="検索..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                        </div>
                        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
                            <SelectTrigger className="w-32">
                                <SelectValue placeholder="ステータス" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">全て</SelectItem>
                                <SelectItem value="new">新規</SelectItem>
                                <SelectItem value="in_progress">対応中</SelectItem>
                                <SelectItem value="resolved">完了</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as any)}>
                            <SelectTrigger className="w-40">
                                <SelectValue placeholder="種類" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">全て</SelectItem>
                                <SelectItem value="pricing">料金プラン相談</SelectItem>
                                <SelectItem value="feature">機能について</SelectItem>
                                <SelectItem value="support">技術サポート</SelectItem>
                                <SelectItem value="other">その他</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* List */}
            <Card>
                <CardContent className="p-0">
                    {filtered.length === 0 ? (
                        <div className="text-center py-12 text-slate-400">
                            <Trans>No inquiries</Trans>
                        </div>
                    ) : (
                        <div className="divide-y">
                            {filtered.map(inquiry => (
                                <div
                                    key={inquiry.id}
                                    className={cn(
                                        "p-4 hover:bg-slate-50 transition-colors",
                                        inquiry.status === 'new' && "bg-blue-50/50"
                                    )}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Select
                                                    value={inquiry.status}
                                                    onValueChange={(v) => handleStatusChange(inquiry.id, inquiry.workspaceId, v as InquiryStatus)}
                                                >
                                                    <SelectTrigger className="h-6 w-24 text-xs">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="new">新規</SelectItem>
                                                        <SelectItem value="in_progress">対応中</SelectItem>
                                                        <SelectItem value="resolved">完了</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <Badge variant="outline" className="text-xs">
                                                    {i18n._(INQUIRY_TYPE_LABELS[inquiry.type])}
                                                </Badge>
                                                <span className="text-xs text-slate-400">
                                                    {inquiry.workspaceName}
                                                </span>
                                            </div>
                                            <h4 className="font-medium text-slate-900">
                                                {inquiry.subject}
                                            </h4>
                                            <p className="text-sm text-slate-500 line-clamp-1 mt-0.5">
                                                {inquiry.message}
                                            </p>
                                        </div>
                                        <div className="flex flex-col items-end gap-2">
                                            <div className="text-xs text-slate-400">
                                                {formatDate(inquiry.createdAt)}
                                            </div>
                                            {inquiry.messages && inquiry.messages.length > 0 && (
                                                <span className="text-xs text-slate-400">
                                                    最終: {formatDate(inquiry.messages[inquiry.messages.length - 1].createdAt)}
                                                </span>
                                            )}
                                            <div className="flex gap-1">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => {
                                                        setSelectedInquiry(inquiry);
                                                        setInternalNote(inquiry.internalNote || '');
                                                        setIsMemoOpen(true);
                                                    }}
                                                >
                                                    <StickyNote className="w-3 h-3 mr-1" />
                                                    メモ
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        setSelectedInquiry(inquiry);
                                                        setEditedStatus(inquiry.status);
                                                        setNewMessage('');
                                                        setIsDetailOpen(true);
                                                    }}
                                                    className="flex items-center gap-1"
                                                >
                                                    <MessageCircle className="w-3 h-3" />
                                                    {inquiry.messages?.length || 0}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Detail Dialog */}
            <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{selectedInquiry?.subject}</DialogTitle>
                        <DialogDescription>
                            {selectedInquiry?.workspaceName}
                            {' · '}{selectedInquiry && i18n._(INQUIRY_TYPE_LABELS[selectedInquiry.type])}
                            {' · '}{selectedInquiry && formatDate(selectedInquiry.createdAt)}
                        </DialogDescription>
                    </DialogHeader>

                    {selectedInquiry && (
                        <div className="space-y-4">

                            <div className="p-4 bg-slate-50 rounded-lg">
                                <div className="text-sm text-slate-500 mb-1">メッセージ:</div>
                                <div className="text-slate-900 whitespace-pre-wrap">
                                    {selectedInquiry.message}
                                </div>
                            </div>

                            {selectedInquiry.snapshot && (
                                <div className="p-3 bg-slate-50 rounded-lg text-xs space-y-1">
                                    <div className="font-medium text-slate-500">問い合わせ時点の情報:</div>
                                    <div>プラン: {selectedInquiry.snapshot.tier}</div>
                                    <div>メンバー: {selectedInquiry.snapshot.memberCount}人</div>
                                </div>
                            )}

                            {/* 쓰레드 대화 */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                    対話履歴
                                    <span className="text-xs text-blue-600 font-normal">(お客様に表示されます)</span>
                                </label>

                                {/* 메시지 목록 */}
                                <div className="max-h-48 overflow-y-auto space-y-2 p-2 bg-slate-50 rounded-lg">
                                    {(!selectedInquiry.messages || selectedInquiry.messages.length === 0) ? (
                                        <div className="text-sm text-slate-400 text-center py-2">
                                            まだ返信はありません
                                        </div>
                                    ) : (
                                        selectedInquiry.messages.map((msg) => (
                                            <div
                                                key={msg.id}
                                                className={cn(
                                                    "p-2 rounded-lg text-sm",
                                                    msg.authorRole === 'admin'
                                                        ? "bg-indigo-100 text-indigo-900 ml-4"
                                                        : "bg-white border mr-4"
                                                )}
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={cn(
                                                        "text-xs font-medium",
                                                        msg.authorRole === 'admin' ? "text-indigo-600" : "text-slate-600"
                                                    )}>
                                                        {msg.authorName}
                                                    </span>
                                                    <span className="text-xs text-slate-400">
                                                        {new Date(msg.createdAt).toLocaleString('ja-JP')}
                                                    </span>
                                                </div>
                                                <div className="whitespace-pre-wrap">{msg.content}</div>
                                            </div>
                                        ))
                                    )}
                                </div>

                                {/* 새 메시지 입력 */}
                                <div className="flex gap-2">
                                    <Textarea
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        placeholder="返信を入力..."
                                        rows={2}
                                        className="flex-1"
                                    />
                                    <Button
                                        onClick={handleSendMessage}
                                        disabled={saving || !newMessage.trim()}
                                        size="sm"
                                        className="self-end"
                                    >
                                        <Send className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDetailOpen(false)}>
                            <Trans>Close</Trans>
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Memo Dialog */}
            <Dialog open={isMemoOpen} onOpenChange={setIsMemoOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <StickyNote className="w-5 h-5" />
                            内部メモ
                        </DialogTitle>
                        <DialogDescription>
                            {selectedInquiry?.subject}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <Textarea
                            value={internalNote}
                            onChange={(e) => setInternalNote(e.target.value)}
                            placeholder="内部メモを入力..."
                            rows={4}
                        />
                        <p className="text-xs text-slate-400">
                            このメモは管理者のみが閲覧できます。お客様には表示されません。
                        </p>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsMemoOpen(false)}>
                            <Trans>Close</Trans>
                        </Button>
                        <Button
                            onClick={handleSaveMemo}
                            disabled={saving}
                        >
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            <Trans>Save</Trans>
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SuperAdminInquiriesPage;
